import React from "react";
import { Box, Typography, Button, styled } from "@mui/material";
import { FlashIcon } from "../../icons/FlashIcon";
import type { MSHatData } from "../../../types";

interface PerkQuirkCoverContentProps {
  hat: MSHatData;
  onStart: () => void;
  xpValue: number;
}

const ContentContainer = styled(Box, {
  label: "PerkQuirkCoverContentContainer",
})(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(2),
  width: "100%",
  maxWidth: "1280px",
  padding: theme.spacing(3),
}));

const MainTitle = styled(Typography, {
  label: "PerkQuirkMainTitle",
})(() => ({
  fontFamily: "Nunito",
  fontWeight: 700,
  fontSize: "32px",
  lineHeight: "48px",
  color: "#ffffff",
  textAlign: "center",
  width: "381px",
}));

const XPSection = styled(Box, {
  label: "PerkQuirkXPSection",
})(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
}));

const XPText = styled(Typography, {
  label: "PerkQuirkXPText",
})(() => ({
  fontFamily: "Proxima Nova",
  fontWeight: 600,
  fontSize: "18px",
  lineHeight: "28px",
  color: "#118d57",
}));

const CoverImage = styled(Box, {
  label: "PerkQuirkCoverImage",
})(({ theme }) => ({
  width: "562px",
  height: "313px",
  borderRadius: theme.spacing(2),
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  border: "4px solid #ffffff",
  boxShadow:
    "0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)",
  maxWidth: "100%",
  "@media (max-width: 600px)": {
    width: "100%",
    height: "200px",
  },
}));

const DescriptionText = styled(Typography, {
  label: "PerkQuirkDescriptionText",
})(() => ({
  fontFamily: "Proxima Nova",
  fontWeight: 600,
  fontSize: "18px",
  lineHeight: "28px",
  color: "#ffffff",
  textAlign: "center",
  width: "571px",
  maxWidth: "100%",
}));

const ButtonContainer = styled(Box, {
  label: "PerkQuirkButtonContainer",
})(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  gap: theme.spacing(7),
  width: "100%",
}));

const StartButton = styled(Button, {
  label: "PerkQuirkStartButton",
})(({ theme }) => ({
  backgroundColor: "#078dee",
  color: "#ffffff",
  fontFamily: "Proxima Nova",
  fontWeight: 700,
  fontSize: "15px",
  lineHeight: "26px",
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(1),
  minWidth: "157px",
  height: "48px",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#0570c4",
  },
}));

const defaultPerkQuirkImage =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTYyIiBoZWlnaHQ9IjMxMyIgdmlld0JveD0iMCAwIDU2MiAzMTMiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI1NjIiIGhlaWdodD0iMzEzIiByeD0iMTYiIGZpbGw9IiNGNEY2RjgiLz4KPHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEwMCIgeD0iMTgxIiB5PSIxMDYuNSI+Cjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjOTE5RUFCIiBmb250LWZhbWlseT0ic2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNiI+UGVya3MgJiBRdWlya3M8L3RleHQ+Cjwvc3ZnPgo8L3N2Zz4K";

export const PerkQuirkCoverContent = ({
  hat,
  onStart,
  xpValue,
}: PerkQuirkCoverContentProps) => {
  const image = hat.perksQuirksDisplayImage?.url || defaultPerkQuirkImage;

  return (
    <ContentContainer>
      <MainTitle>Perks & Quirks</MainTitle>

      <XPSection>
        <FlashIcon width={24} color="#118d57" />
        <XPText>{xpValue}XP</XPText>
      </XPSection>

      <CoverImage
        sx={{
          backgroundImage: `url('${image}')`,
        }}
      />

      <DescriptionText>
        Explore the benefits and challenges of being a {hat.displayName}
      </DescriptionText>

      <ButtonContainer>
        <StartButton onClick={onStart}>Start</StartButton>
      </ButtonContainer>
    </ContentContainer>
  );
};
